/* AO - SHOP mega menu behaviour.
 *
 * Deliberately its own file rather than another block in `custom.js`. That file
 * is a single shared scope where one throwing statement silently kills every
 * later block - it has already cost this project a broken variant picker - and
 * a nav component is the last thing that should be able to take the rest of the
 * page down with it.
 *
 * OPENING IS ON CLICK, NOT HOVER, and that is the point of the change. Pete's
 * complaint about the old row was that it was "difficult to click and navigate
 * (very spacy)": eight hover targets, each only as wide as its own text, with
 * 40px of dead space between them, so the pointer kept falling into a gap and
 * dropping the menu. A click-opened panel cannot be lost by accident. Set
 * `data-open-on-hover` on the element to get the old behaviour back with an
 * intent delay, if that ever turns out to be wanted.
 *
 * Once the panel IS open, the rail switches on hover as well as click. That is
 * safe where the top row was not, because the rail is a contiguous column of
 * full-width buttons with no gaps between them.
 */
(function () {
  var HOVER_OPEN_DELAY = 150; // ms, only used when data-open-on-hover is set
  var HOVER_CLOSE_DELAY = 300; // ms of grace before an unhovered panel closes

  var Component = class extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.ao-shop__trigger');
      this.panel = this.querySelector('.ao-shop__panel');
      this.railButtons = Array.prototype.slice.call(this.querySelectorAll('.ao-shop__rail-button'));
      this.panes = Array.prototype.slice.call(this.querySelectorAll('.ao-shop__pane'));
      this.openTimer = null;
      this.closeTimer = null;

      if (this.trigger && this.panel) {
        this.trigger.addEventListener('click', this._onTriggerClick.bind(this));
      }

      this.railButtons.forEach(
        function (button, index) {
          button.addEventListener('click', this._selectIndex.bind(this, index, true));
          button.addEventListener('mouseenter', this._selectIndex.bind(this, index, false));
          button.addEventListener('keydown', this._onRailKeydown.bind(this, index));
        }.bind(this)
      );

      // The flat items that have children (Help, Sale and so on) get a plain
      // disclosure. Same open/close rules, so the whole bar behaves one way.
      this.disclosures = Array.prototype.slice.call(
        this.querySelectorAll('.ao-shop__top-item--has-menu > .ao-shop__top-link')
      );
      this.disclosures.forEach(
        function (button) {
          button.addEventListener('click', this._onDisclosureClick.bind(this, button));
        }.bind(this)
      );

      // Teaser images are `loading="lazy"` inside a `hidden` panel, and a lazy
      // image in a hidden subtree NEVER STARTS LOADING - so without this the
      // first open showed eight empty boxes while they fetched. Hover and focus
      // both reliably precede the click that opens it, so flipping them to
      // eager on intent means they are decoded by the time the panel appears.
      // Once only: after the first run the images are already loading.
      this._primed = false;
      var prime = this._primeImages.bind(this);
      this.addEventListener('pointerenter', prime);
      this.addEventListener('focusin', prime);

      if (this.hasAttribute('data-open-on-hover')) {
        this.addEventListener('mouseenter', this._onHoverIn.bind(this));
      }
      this.addEventListener('mouseleave', this._onHoverOut.bind(this));

      this.addEventListener('keydown', this._onKeydown.bind(this));
      this._onDocumentClick = this._onDocumentClick.bind(this);
      document.addEventListener('click', this._onDocumentClick);

      // Garage capture and the "Shopping for" chip are BOTH handled in
      // ao-garage.js now, delegated at the document, so the mobile drawer gets
      // them for free. Binding them here as well would double-count every click.
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
    }

    _primeImages() {
      if (this._primed) return;
      this._primed = true;
      // Both the old category teasers and the featured-product images. A lazy
      // image inside a `hidden` panel never starts loading, so missing a
      // selector here means empty boxes on first open - which is exactly what
      // happened when the featured images were added and this was not updated.
      var images = this.querySelectorAll(
        '.ao-shop__card-image[loading="lazy"], .ao-shop__featured-image[loading="lazy"]'
      );
      Array.prototype.forEach.call(images, function (img) {
        img.loading = 'eager';
      });
    }

    /* --- opening and closing ------------------------------------------- */

    get isOpen() {
      return this.trigger && this.trigger.getAttribute('aria-expanded') === 'true';
    }

    open() {
      if (!this.trigger || !this.panel || this.isOpen) return;
      this._closeDisclosures();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      this.classList.add('is-open');
      this._selectRememberedOnce();
    }

    /* Land on the customer's own vehicle the FIRST time the panel is opened on
       a page, rather than always on whatever is top of the rail.

       Deliberately once per page. Browsing has to stay free: someone who owns a
       Model 3 and is looking at Model Y parts must not be dragged back to Model
       3 every time they reopen the panel. Confirmed 2026-08-08 that the pane
       following the browse is correct behaviour, not a bug - this only changes
       the starting point. */
    _selectRememberedOnce() {
      if (this._landed) return;
      this._landed = true;
      if (!window.AOGarage) return;

      var garage = window.AOGarage.get();
      if (!garage || !garage.modelHandle) return;

      for (var i = 0; i < this.panes.length; i++) {
        if (this.panes[i].getAttribute('data-vehicle-handle') === garage.modelHandle) {
          this._selectIndex(i, false);
          return;
        }
      }
    }

    close() {
      window.clearTimeout(this.openTimer);
      if (!this.trigger || !this.panel) return;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      this.classList.remove('is-open');
    }

    _onTriggerClick(event) {
      event.preventDefault();
      if (this.isOpen) this.close();
      else this.open();
    }

    _onHoverIn() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.openTimer);
      this.openTimer = window.setTimeout(this.open.bind(this), HOVER_OPEN_DELAY);
    }

    _onHoverOut() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      // Only hover-opened menus close themselves on the way out. A menu the
      // customer deliberately clicked open stays open until they say otherwise,
      // which is the behaviour the old row got wrong.
      if (!this.hasAttribute('data-open-on-hover')) return;
      this.closeTimer = window.setTimeout(
        function () {
          this.close();
          this._closeDisclosures();
        }.bind(this),
        HOVER_CLOSE_DELAY
      );
    }

    _onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.close();
      this._closeDisclosures();
    }

    _onKeydown(event) {
      if (event.key !== 'Escape') return;
      var wasOpen = this.isOpen || this._openDisclosure();
      this.close();
      this._closeDisclosures();
      if (wasOpen && this.trigger) this.trigger.focus();
    }

    /* --- the vehicle rail ----------------------------------------------- */

    _selectIndex(index, fromClick, event) {
      if (event && fromClick) event.preventDefault();

      this.railButtons.forEach(function (button, i) {
        var active = i === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.panes.forEach(function (pane, i) {
        pane.hidden = i !== index;
      });

      if (fromClick) this.railButtons[index].focus();
    }

    _onRailKeydown(index, event) {
      var last = this.railButtons.length - 1;
      var next = null;

      if (event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
        // Move into the pane rather than leaving the rail as a dead end.
        var pane = this.panes[index];
        var firstLink = pane && pane.querySelector('a[href]');
        if (firstLink) {
          event.preventDefault();
          firstLink.focus();
        }
        return;
      } else return;

      event.preventDefault();
      this._selectIndex(next, true);
    }

    /* --- the flat disclosures ------------------------------------------- */

    _openDisclosure() {
      for (var i = 0; i < this.disclosures.length; i++) {
        if (this.disclosures[i].getAttribute('aria-expanded') === 'true') return this.disclosures[i];
      }
      return null;
    }

    _closeDisclosures() {
      this.disclosures.forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
        var list = document.getElementById(button.getAttribute('aria-controls'));
        if (list) list.hidden = true;
      });
    }

    _onDisclosureClick(button, event) {
      event.preventDefault();
      var isOpen = button.getAttribute('aria-expanded') === 'true';
      this.close();
      this._closeDisclosures();
      if (isOpen) return;
      button.setAttribute('aria-expanded', 'true');
      var list = document.getElementById(button.getAttribute('aria-controls'));
      if (list) list.hidden = false;
    }
  };

  if (!window.customElements.get('ao-shop-menu')) {
    window.customElements.define('ao-shop-menu', Component);
  }
})();
